/**
 * Created by Abner Sui on 10/16/2019
 * -------------------------------------
 */

import { Component, OnInit, Input, OnDestroy, ViewChild, AfterViewInit } from '@angular/core';
import { FieldActions, FieldConfig, FieldEvents } from './field.model';
import { TooltipDirective } from '@progress/kendo-angular-tooltip';
import { Subscription, fromEvent } from 'rxjs';
import { UtilsService } from '@tamalelibs/services/utils.service';
import { DeviceDetectorService } from 'ngx-device-detector';

@Component({
    selector: 'tam-date-field',
    templateUrl: './date-field.component.html',
    styleUrls: ['./field.component.scss', './date-field.component.scss'],
    standalone: false
})
export class DateFieldComponent implements OnInit, AfterViewInit, OnDestroy {

    @Input() config: FieldConfig;
    @Input() viewMode: 'viewOnly' | 'full' = 'viewOnly';
    @ViewChild('anchor', { static: false }) anchor;
    @ViewChild(TooltipDirective, { static: false })
    tooltipDir: TooltipDirective;
    isIPAD = false;
    requireInvalid = false;

    private _destroySubscriptions: Array<Subscription> = [];

    constructor(
        private _utils: UtilsService,
        private _deviceService: DeviceDetectorService,
    ) { }

    ngOnInit() {
        if (this.config.disabled) {
            return;
        }
        this.isIPAD = this._deviceService.isMobile() || this._deviceService.isTablet();
        this._destroySubscriptions.push(
            this.config.config.actionSubject$.subscribe(action => this._onAction(action)),
        );
        this.config.config.feedbackSubject$.next({
            type: FieldEvents.VALIDATE_CHANGE,
            payload: {
                id: this.config.field.fieldDefinition.id,
                invalid: this.validateRequire(),
            },
        });
    }

    ngAfterViewInit() {
        if (this.isIPAD && this.config.field.fieldDefinition.description) {
            this._destroySubscriptions.push(
                fromEvent(document, 'click').subscribe((event) => {
                    this.showTooltip(event, this.anchor, false);
                }),
            );
        }
    }

    ngOnDestroy() {
        this._destroySubscriptions.forEach(item => item.unsubscribe());
    }

    showTooltip(event, target, show: boolean): void {
        if (show === null) {
            this.tooltipDir.toggle(target);
        } else {
            this.tooltipDir.toggle(target, show);
        }
        this._utils.emptyClick(event);
    }

    validateRequire(): boolean {
        if (this.config.editable && this.config.required && (this.config.field.value === null || this.config.field.value === undefined)) {
            return true;
        } else {
            return false;
        }
    }

    valueChange(event) {
        this.requireInvalid = false;
        if (!this._isIncludeHMS(event)) {
            const currentDate = new Date();
            event = new Date(event.getFullYear(), event.getMonth(), event.getDate(), currentDate.getHours(), currentDate.getMinutes(), currentDate.getSeconds());
            this.config.field.value = event;
        }
        this.config.config.feedbackSubject$.next({
            type: FieldEvents.VALUE_CHANGE,
            payload: {
                id: this.config.field.fieldDefinition.id,
                value: event,
            },
        });

        this.config.config.feedbackSubject$.next({
            type: FieldEvents.VALIDATE_CHANGE,
            payload: {
                id: this.config.field.fieldDefinition.id,
                invalid: this.validateRequire(),
            },
        });
    }

    // Kendo v19: on focus, select the first date segment (month) instead of the entire value.
    onDateFocus(event: any): void {
        setTimeout(() => {
            const el = event?.target as HTMLElement;
            const input = el?.tagName === 'INPUT' ? el as HTMLInputElement :
                el?.querySelector?.('input.k-input-inner') as HTMLInputElement;
            if (!input) {
                return;
            }
            const value = input.value || '';
            const firstSep = value.search(/[\/\-\.]/);
            if (firstSep > 0) {
                input.setSelectionRange(0, firstSep);
            }
        });
    }

    // Kendo v19: Tab no longer navigates between date segments (month/day/year).
    // Restore old behavior by intercepting Tab and moving selection to the next/previous segment.
    onDateKeydown(event: KeyboardEvent): void {
        if (event.key !== 'Tab') {
            return;
        }
        const input = event.target as HTMLInputElement;
        if (!input || input.tagName !== 'INPUT') {
            return;
        }
        const value = input.value || '';
        // Find separator positions (e.g. '/' in 'MM/DD/YYYY')
        const seps: number[] = [];
        for (let i = 0; i < value.length; i++) {
            if (value[i] === '/' || value[i] === '-' || value[i] === '.') {
                seps.push(i);
            }
        }
        if (seps.length < 2) {
            return;
        }
        // Build segment ranges: [{start, end}, ...]
        const segments = [
            { start: 0, end: seps[0] },
            { start: seps[0] + 1, end: seps[1] },
            { start: seps[1] + 1, end: value.length },
        ];
        // Determine which segment the cursor is currently in
        const selStart = input.selectionStart;
        const selEnd = input.selectionEnd;
        // If entire value is selected (initial focus state), select first segment
        if (selStart === 0 && selEnd === value.length && !event.shiftKey) {
            event.preventDefault();
            input.setSelectionRange(segments[0].start, segments[0].end);
            return;
        }
        let currentSeg = segments.length - 1;
        for (let i = 0; i < segments.length; i++) {
            if (selStart <= segments[i].end) {
                currentSeg = i;
                break;
            }
        }
        if (event.shiftKey) {
            // Shift+Tab: move to previous segment, or let Tab exit if already on first
            if (currentSeg > 0) {
                event.preventDefault();
                const prev = segments[currentSeg - 1];
                input.setSelectionRange(prev.start, prev.end);
            }
        } else {
            // Tab: move to next segment, or let Tab exit if already on last
            if (currentSeg < segments.length - 1) {
                event.preventDefault();
                const next = segments[currentSeg + 1];
                input.setSelectionRange(next.start, next.end);
            }
        }
    }

    private _isIncludeHMS(date: Date): boolean {
        let isIncludeHMS = false;
        const hours = date.getHours();
        const minutes = date.getMinutes();
        const seconds = date.getSeconds();
        if (hours === 0 && minutes === 0 && seconds === 0) {
            isIncludeHMS = false;
        } else {
            isIncludeHMS = true;
        }
        return isIncludeHMS;
    }

    private _onAction(action) {
        if (action.type === FieldActions.SHOW_VALIDATE_REQUIRE) {
            this.requireInvalid = this.validateRequire();
        }
    }
}
